import React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { ContentTableProps, ContentTableState } from "./models";

import {
	Box,
	Chip,
	ListItemIcon,
	MenuItem,
	Tooltip,
	Typography
} from "@mui/material";

import { ContentDescription, ContentStatus } from "models";

import MaterialReactTable from 'material-react-table';
import type { MRT_ColumnDef } from 'material-react-table';

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from '@mui/icons-material/Delete';
import VideocamIcon from '@mui/icons-material/Videocam';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import ErrorIcon from '@mui/icons-material/Error';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { AccessTime, Bookmark, CheckCircle, CloudUpload, Pending, DoNotDisturbOn } from "@mui/icons-material";


import {t, Trans} from "@lingui/macro";

import { ContentType, CONTENT_TYPE_FORM_CONSTANT, SEGREGATION_TYPE_FORM_CONSTANT } from "commons/form-constants";

import { selectDescriptionByLanguage } from "utils";
import {ConfirmModal} from "commons/modal/confirm-modal";
import AuthenticationService from "services/authentication-service";


class ContentTable extends React.Component<ContentTableProps & RouteComponentProps<any>, ContentTableState>
{
	private tableRef: any;

	private tableColumns: MRT_ColumnDef[];

	private isAtLeastChiefProducer: boolean;

	private parseSegregationForm = (value: string) => {

		for (let element of SEGREGATION_TYPE_FORM_CONSTANT) {
			if (value === element.key)
				return element.label
		}

		return "/"
	}

	private parseContentType = (value: string) => {

		for (let element of CONTENT_TYPE_FORM_CONSTANT) {
			if (value === element.key)
				return element.label
		}

		return "/"
	}

	private getLanguageValue = (row: any): string => {
		return row.original.language
	}

	private getIdValue = (row: any): string => {
		return row.original.id
	}

	private getStatus = (row: any): ContentStatus => {
		return row.original.status
	}

	private goEdit = (row: any): void => {
		let { history } = this.props
		history.push(`/backoffice/edit-content/${this.getIdValue(row)}`);
	}

	public constructor(props: any) {

		super(props)

		this.state = {
			loaded: true,
			showSetFavoriteModal: false,
			favoriteContent: null,
			openConfirmModal: false,
			contentToDelete: ""
		}

		this.isAtLeastChiefProducer = !AuthenticationService.isNotAtLeastChiefProducer()

		this.tableColumns = [
			{
				header: t({
					id: "ct-table-col-title",
					message: "Titolo"
				}),
				accessorKey: "title",
				Cell: ({ cell, row }) => {
					return (
						<Typography>
							{selectDescriptionByLanguage(cell.getValue<ContentDescription[]>(), this.getLanguageValue(row))}
						</Typography>
					);
				},
			},
			{
				header: t({
					id: "ct-table-col-type",
					message: "Tipologia"
				}),
				accessorKey: "type",
				Cell: ({ cell }) => {

					let value = cell.getValue<ContentType>();
					return (
						<Box>
							{value === ContentType.VOD && <Chip avatar={<VideocamIcon style={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />}
								label={t({
									id: "ct-table-col-type-vod",
									message: "VOD"
								})} color="info" />}
							{value === ContentType.LIVE && <Chip avatar={<LiveTvIcon style={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />}
								label={t({
									id: "ct-table-col-type-live",
									message: "Live"
								})} color="info" />}
						</Box>
					);
				},
			},
			{
				header: t({
					id: "ct-table-col-segregation-type",
					message: "Segregazione"
				}),
				accessorKey: "segregationType",
				Cell: ({ cell }) => {
					return (
						<Box>
							{this.parseSegregationForm(cell.getValue<string>())}
						</Box>
					);
				},
			},
			{
				header: t({
					id: "ct-table-col-status",
					message: "Stato"
				}),
				accessorKey: "status",
				Cell: ({ cell }) => {

					let value = cell.getValue<ContentStatus>();

					return (
						<Box>
							{value === ContentStatus.READY && <Chip avatar={<CheckCircle style={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />}
								label={t({
									id: "ct-table-col-status-ready",
									message: "Pronto"
								})} color="success" />}
							{value === ContentStatus.ERROR && <Chip avatar={<ErrorIcon style={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />} label={t({
								id: "ct-table-col-status-error",
								message: "Errore"
							})} color="error" />}
							{value === ContentStatus.PENDING_THUMB_UPLOAD && <Chip avatar={<CloudUpload style={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />} label={t({
								id: "ct-table-col-status-pending-thumb",
								message: "Caricamento thumbnail in corso"
							})} color="warning" />}
							{value === ContentStatus.PENDING_UPLOAD && <Chip avatar={<CloudUpload style={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />} label={t({
								id: "ct-table-col-status-pending-upload",
								message: "Caricamento in corso"
							})} color="warning" />}
							{value === ContentStatus.PENDING_ENCODING && <Chip avatar={<AccessTime style={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />} label={t({
								id: "ct-table-col-status-pending-encoding",
								message: "Transcodifica in corso"
							})} color="info" />}
							{value === ContentStatus.PENDING_APPROVAL && <Chip avatar={<Pending style={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />} label={t({
								id: "ct-table-col-status-pending-approval",
								message: "In attesa di approvazione"
							})} color="warning" />}
							{value === ContentStatus.NOT_APPROVED && <Chip avatar={<DoNotDisturbOn style={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />} label={t({
								id: "ct-table-col-status-not-approved",
								message: "Non approvato"
							})} color="error" />}
						</Box>
					);
				},
			},
			{
				header: t({
					id: "ct-table-col-release-date",
					message: "Data di rilascio"
				}),
				accessorKey: "releaseDate",
				Cell: ({ cell }) => {
					return (
						<Box>
							{cell.getValue<string>() && (new Date(cell.getValue<string>())).toLocaleDateString("it-IT")}
						</Box>
					);
				},
			},
			{
				header: t({
					id: "ct-table-col-released",
					message: "Rilasciato"
				}),
				accessorKey: "released",
				Cell: ({ cell }) => {

					let value = cell.getValue<boolean>();

					return (
						<Box>
							{value && <Tooltip title={t({
								id: "ct-table-col-released-tooltip",
								message: "Rilasciato"
							})}>
								<CheckCircle color="success"> </CheckCircle>
							</Tooltip>}
							{!value && <Tooltip title={t({
								id: "ct-table-col-released-waiting-tooltip",
								message: "Il contenuto è in attesa di essere rilasciato"
							})}>
								<Pending color="info"></Pending>
							</Tooltip>}
						</Box>
					);
				},
			},
		];
	}

	private handlePageChange = (updateFn: any) => {
			let newPagination = updateFn(this.props.pagination);
			this.props.updatePaginationCallBackFn(newPagination);
	}

	private openConfirmModal = (id: string) : void =>{
		this.setState(() => ({
			openConfirmModal: true,
			contentToDelete: id
		}))
	}

	private cancelConfirmModalCallback = () : void =>{
		this.setState(() => ({
			openConfirmModal: false,
			contentToDelete: ""
		}))
	}

	private acceptConfirmModal = () : void =>{
		this.setState(() => ({
			openConfirmModal: false,
		}))
		this.deleteContent()
	}

	private deleteContent = (): void => {
		this.props.deleteContent(this.state.contentToDelete);
		this.setState(() => ({
			contentToDelete: ""
		}))

	}


	render() {
		return (
			<>
			<Box style={{ maxWidth: '100%' }}>
				<MaterialReactTable
					columns={this.tableColumns}
					rowCount={this.props.totalContentCount}
					pageCount={Math.ceil(this.props.totalContentCount / this.props.pagination.pageSize)}
					data={this.props.contents}
					enableRowSelection={false}
					enableColumnOrdering
					enableColumnActions={false}
					enableGlobalFilter={false}
					enableFilters={false}
					muiTableHeadCellProps={{
						sx: {
							color: "white",
							backgroundColor: process.env.REACT_APP_PRIMARY_COLOR
						}
					}}
					muiTableHeadRowProps={{
						sx: {
							color: "white",
							backgroundColor: process.env.REACT_APP_PRIMARY_COLOR
						}
					}}
					displayColumnDefOptions={{
						'mrt-row-actions': {
							header: t({
								id: "ct-table-col-actions",
								message: "Azioni"
							})
						}
					}}
					enableRowActions
					positionActionsColumn="last"
					renderRowActionMenuItems={({ row }) => [
						<MenuItem key="set-main" onClick={() => this.props.setMainContent(this.getIdValue(row))}>
							<ListItemIcon>
								<Bookmark color={(row.original as any).isHighlighted ? "success" : undefined} fontSize="small"/>
							</ListItemIcon>
							<Trans id={"ct-table-col-set-main"}>Imposta come principale</Trans>
						</MenuItem>,
						<MenuItem key="edit" onClick={() => this.goEdit(row)}>
							<ListItemIcon>
								<EditIcon fontSize="small"/>
							</ListItemIcon>
							<Trans id={"ct-table-col-edit"}>Modifica</Trans>
						</MenuItem>,
						<MenuItem key="delete" onClick={() => this.openConfirmModal(this.getIdValue(row))}>
							<ListItemIcon>
								<DeleteIcon fontSize="small"/>
							</ListItemIcon>
							<Trans id={"ct-table-col-delete"}>Elimina</Trans>
						</MenuItem>,
						this.isAtLeastChiefProducer && this.getStatus(row)===ContentStatus.PENDING_APPROVAL && <MenuItem key="approve" onClick={() => this.props.approveContent(true, this.getIdValue(row))}>
							<ListItemIcon>
								<CheckIcon fontSize="small"/>
							</ListItemIcon>
							<Trans id={"ct-table-col-approve"}>Approva</Trans>
						</MenuItem>,
						this.isAtLeastChiefProducer && this.getStatus(row)===ContentStatus.PENDING_APPROVAL && <MenuItem key="reject" onClick={() => this.props.approveContent(false, this.getIdValue(row))}>
							<ListItemIcon>
								<CloseIcon fontSize="small"/>
							</ListItemIcon>
							<Trans id={"ct-table-col-reject"}>Rifiuta</Trans>
						</MenuItem>
					]}
					onPaginationChange={this.handlePageChange}
					enableTopToolbar={false}
					manualPagination={true}
					autoResetPageIndex={false}
					enableDensityToggle={false}
					state={{
						pagination: this.props.pagination
					}} />
			</Box>
		{this.state.openConfirmModal && < ConfirmModal
			cancelConfirmCallback={this.cancelConfirmModalCallback}
			confirmCallback={this.acceptConfirmModal}
			text={"Sei sicuro di voler procedere con l'eliminazione del contenuto?"}
			textId={"ct-delete-content-text"}
		/>}
	</>)
	}
}

export default withRouter(ContentTable);